import { atom, map, type PreinitializedMapStore } from 'nanostores'

import type { Conversation, Message, UpdateConversation } from './types'

const $conversations = map<Record<string, Conversation>>({})
const $messages = map<Record<string, Message>>({})
const $conversationCursors = atom<(null | string)[]>([])

function addConversationCursor(id: null | string) {
	const cursors = $conversationCursors.get()
	$conversationCursors.set([...cursors, id])
}

const resetConversationCursors = () => $conversationCursors.set([])

function addToMap<I>({
	id,
	isChanged,
	item,
	map
}: {
	id: string
	isChanged?: (first: I, second: I) => boolean
	item: I
	map: PreinitializedMapStore<Record<string, I>>
}) {
	const store = map.get()
	const shouldUpdate = id in store
	if (!shouldUpdate) {
		return map.setKey(id, item)
	}
	const prev = store[id]

	if (!isChanged || isChanged(prev, item)) {
		map.setKey(id, item)
	}
}

const isConversationChanged = (first: Conversation, second: Conversation) =>
	first.last_activity_at !== second.last_activity_at ||
	first.unread_count !== second.unread_count

const saveConversation = (conversation: Conversation) =>
	addToMap({
		id: conversation.entity_id,
		isChanged: isConversationChanged,
		item: conversation,
		map: $conversations
	})

const saveMessage = (message: Message) =>
	addToMap({
		id: message.message_id,
		isChanged: (first, second) =>
			JSON.stringify(first) !== JSON.stringify(second),
		item: message,
		map: $messages
	})

function updateOne<I>({
	id,
	map,
	patch
}: {
	id: string
	map: PreinitializedMapStore<Record<string, I>>
	patch: Partial<I>
}) {
	const store = map.get()
	const current = store[id]

	if (current) {
		map.setKey(id, {
			...current,
			...patch
		})
	}
}

const updateConversation = (id: string, patch: Partial<Conversation>) =>
	updateOne({ id, map: $conversations, patch })

const updateMessage = (id: string, patch: Partial<Message>) =>
	updateOne({ id, map: $messages, patch })

function updateRead({ id, mark }: UpdateConversation) {
	updateConversation(id, { unread_count: Number(!mark) })
}

function updateStar({ id, mark }: UpdateConversation) {
	updateConversation(id, { is_starred: mark })
}

export {
	$conversations,
	$messages,
	addConversationCursor,
	resetConversationCursors,
	saveConversation,
	saveMessage,
	updateMessage,
	updateRead,
	updateStar
}
